import type { AiCall, AiCallOrder } from "@pax/api-contract/ai/calls/v1alpha1";
import { focusHaloFlush, panelMotion, press } from "@pax/ui";
import { Plus, X } from "lucide-react";
import { useState } from "react";
import { Failure } from "@/features/evals/components";
import { CallList } from "./Calls";
import { Chart, changeText, Legend, reading } from "./Chart";
import {
  aboutOf,
  type CallFilter,
  type Dimension,
  dimensions,
  iconOf,
  type Keep,
  nameOf,
  pinned,
} from "./dimensions";
import {
  bestSplit,
  type ChartSpec,
  emptyTotals,
  hasCalls,
  type Metric,
  metricIds,
  metrics,
  profileCharts,
  type Split,
  splitGroups,
  splitLabel,
  splitsByTokenKind,
} from "./measures";
import { capitalized, Facts, Mark, Picker } from "./pieces";
import {
  bucketName,
  type CallShow,
  type Range,
  ranges,
  type TimeWindow,
  useQuestion,
  useTotals,
  useTotalsBefore,
} from "./queries";

export const defaultCharts = (keep: Keep): ChartSpec[] =>
  profileCharts.map((chart, index) => ({
    id: index,
    ...chart,
    split: bestSplit(chart.metric, keep),
  }));

/**
 * One chart on a profile, asked the same way as the page's own question: a measure, a grouping,
 * and a way to take it off the page.
 */
function ProfileChart({
  spec,
  filter,
  window,
  onChange,
  onRemove,
  onOpen,
}: {
  spec: ChartSpec;
  filter: CallFilter;
  window: TimeWindow;
  onChange: (spec: ChartSpec) => void;
  onRemove: () => void;
  onOpen: (dimension: Dimension, value: string) => void;
}) {
  const [bucket, setBucket] = useState<number | null>(null);
  const [hoveredRow, setHoveredRow] = useState<string | null>(null);
  const { metric, slowest, split } = spec;
  const info = metrics[metric];
  const question = useQuestion(filter, split, window, info, slowest);
  const before = useTotalsBefore(filter, window);
  const shaped = question.shaped;
  const now = bucket === null ? shaped?.window : (shaped?.buckets[bucket] ?? emptyTotals());
  const bucketFrom = window.from + (bucket ?? 0) * window.bucketMs;

  const pickMetric = (next: Metric) =>
    onChange({
      ...spec,
      metric: next,
      split:
        split === "tokenKind" && !splitsByTokenKind(next) ? bestSplit(next, filter.keep) : split,
    });

  return (
    <section className="group/chart mt-10">
      <div className="flex items-start justify-between gap-4">
        <div className="-ml-3 flex min-w-0 flex-wrap items-center text-sm text-muted-foreground">
          <Picker
            label={info.label}
            groups={[
              metricIds.map(id => ({ id, label: metrics[id].label, selected: id === metric })),
            ]}
            onPick={id => pickMetric(id as Metric)}
          />
          {metric === "firstToken" && (
            <>
              at the
              <Picker
                label={slowest ? "95th percentile" : "median"}
                groups={[
                  [
                    { id: "median", label: "median", selected: !slowest },
                    { id: "p95", label: "95th percentile", selected: slowest },
                  ],
                ]}
                onPick={id => onChange({ ...spec, slowest: id === "p95" })}
              />
            </>
          )}
          grouped by
          <Picker
            label={splitLabel(split)}
            groups={splitGroups(metric, split, filter.keep)}
            onPick={id => onChange({ ...spec, split: id as Split })}
          />
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <span className="text-xl font-semibold tracking-tight tabular-nums">
            {now ? reading(info, now, slowest) : "–"}
          </span>
          <button
            type="button"
            aria-label="Remove this chart"
            title="Remove this chart"
            onClick={onRemove}
            className={`-mr-2 grid size-7 place-items-center rounded-full text-muted-foreground opacity-0 group-hover/chart:opacity-100 hover:bg-foreground/[0.07] hover:text-foreground focus-visible:opacity-100 ${panelMotion} ${focusHaloFlush}`}
          >
            <X className="size-3.5" />
          </button>
        </div>
      </div>
      <p className="mt-0.5 h-4 text-right text-xs text-muted-foreground tabular-nums">
        {bucket !== null
          ? bucketName(bucketFrom, bucketFrom + window.bucketMs, window.range)
          : shaped && !slowest
            ? changeText(info, shaped.window, before.data, window.range)
            : ""}
      </p>
      <Failure error={question.error} />
      {shaped && (
        <>
          <Chart
            rows={shaped.rows}
            metric={info}
            slowest={slowest}
            hoveredBucket={bucket}
            onHoverBucket={setBucket}
            hoveredRow={hoveredRow}
          />
          {split !== "none" && (
            <Legend
              rows={shaped.rows}
              metric={info}
              slowest={slowest}
              bucket={bucket}
              onHoverRow={setHoveredRow}
              onOpen={onOpen}
            />
          )}
        </>
      )}
    </section>
  );
}

const PROFILE_CALLS = 8;

/**
 * Everything about one value of a dimension, one measure under the next, each broken down the
 * way that says most about it. Every chart can be asked again.
 */
export function Profile({
  dimension,
  value,
  within,
  window,
  charts,
  onCharts,
  onRange,
  onOpen,
  onOpenCall,
}: {
  dimension: Dimension;
  value: string;
  /** The filter the profile was opened from, which it keeps. */
  within: CallFilter;
  window: TimeWindow;
  charts: ChartSpec[];
  onCharts: (charts: ChartSpec[]) => void;
  onRange: (range: Range) => void;
  onOpen: (dimension: Dimension, value: string) => void;
  onOpenCall: (call: AiCall) => void;
}) {
  const filter: CallFilter = { ...within, keep: { ...within.keep, [dimension]: [value] } };
  const totals = useTotals(filter, window);
  const [callSort, setCallSort] = useState<AiCallOrder>("latest");
  const [callShow, setCallShow] = useState<CallShow>("all");
  const addChart = () =>
    onCharts([
      ...charts,
      {
        id: Math.max(-1, ...charts.map(chart => chart.id)) + 1,
        metric: "spend",
        slowest: false,
        split: pinned("model", filter.keep) ? "tokenKind" : "model",
      },
    ]);
  const keeping = (Object.entries(within.keep) as [Dimension, string[]][]).map(
    ([kept, values]) =>
      `${dimensions[kept].label} ${values.map(id => nameOf(kept, id)).join(" or ")}`,
  );
  const about = aboutOf(dimension, value);

  return (
    <div>
      <div className="flex items-center gap-2.5">
        <Mark src={iconOf(dimension, value)} size="size-6" />
        <h2 className="text-2xl font-semibold tracking-tight">{nameOf(dimension, value)}</h2>
      </div>
      <div className="mt-2 -ml-3 flex flex-wrap items-center text-sm text-muted-foreground">
        <span className="pl-3">
          {capitalized(dimensions[dimension].label)}
          {about ? ` · ${about}` : ""}
          {keeping.length > 0 && ` · only ${keeping.join(", ")}`} · over
        </span>
        <Picker
          label={ranges[window.range].label}
          groups={[
            (Object.keys(ranges) as Range[]).map(id => ({
              id,
              label: ranges[id].label,
              selected: id === window.range,
            })),
          ]}
          onPick={id => onRange(id as Range)}
        />
      </div>

      <Failure error={totals.error} />
      <Facts
        facts={profileCharts.map(({ metric, slowest }) => [
          metric === "firstToken"
            ? `First token, ${slowest ? "p95" : "median"}`
            : metrics[metric].label,
          totals.data && hasCalls(totals.data)
            ? reading(metrics[metric], totals.data, slowest)
            : "–",
        ])}
      />

      {charts.map(spec => (
        <ProfileChart
          key={spec.id}
          spec={spec}
          filter={filter}
          window={window}
          onChange={next => onCharts(charts.map(chart => (chart.id === spec.id ? next : chart)))}
          onRemove={() => onCharts(charts.filter(chart => chart.id !== spec.id))}
          onOpen={onOpen}
        />
      ))}
      <button
        type="button"
        onClick={addChart}
        className={`-mx-3 mt-8 flex h-10 w-[calc(100%+1.5rem)] items-center justify-center gap-1.5 rounded-xl text-sm text-muted-foreground ring-1 ring-foreground/8 ring-inset hover:bg-foreground/5 hover:text-foreground ${panelMotion} ${press} ${focusHaloFlush}`}
      >
        <Plus className="size-3.5" />
        Add a chart
      </button>

      <CallList
        filter={filter}
        window={window}
        sort={callSort}
        show={callShow}
        onSort={setCallSort}
        onShow={setCallShow}
        pageSize={PROFILE_CALLS}
        onOpen={onOpenCall}
      />
    </div>
  );
}
